import { HTTPClient, AxiosResponse } from '@abaco/web-common/src/HTTP';
import { PagedResults, PagingParams } from '@abaco/web-common/src/Paging';
import { Cattle, CattleNode, ConfigurationDeclaration, Effluent, ExcessWater, FarmMaterial, ItemI, ItemLM, MaterialByFarm, Model, NitratesDeclaration, OriginWasteWater, PrintType, Quadro, Region, StorageSituation, Subject, TreatmentItem, TreatmentLine, WasteWater } from '../models/NDModel';
export default class NDApi {
    private http: HTTPClient;
    public context = "FARM_CROP_PLAN_MAP";

    constructor(http: HTTPClient) {
        this.http = http;
    }

    public async getRegions(): Promise<Region[]> {
        const url = `/nitrates-declaration-api/api/v1/data/regions`;
        return (await this.http.get(url)).data
    }

    public async getConfigurationDeclaration(pkCuaa: number, nd: NitratesDeclaration): Promise<ConfigurationDeclaration> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/configurationDeclaration`;
        return (await this.http.get(url)).data
    }

    public async setConfigurationDeclaration(pkCuaa: number, nd: NitratesDeclaration, configuration: ConfigurationDeclaration): Promise<ConfigurationDeclaration> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/configurationDeclaration`;
        return (await this.http.post(url, configuration)).data
    }

    public async getReportingSubject(pkCuaa: number, nd: NitratesDeclaration): Promise<Subject> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/reportingSubject`;
        return (await this.http.get(url)).data
    }

    public async setReportingSubject(pkCuaa: number, nd: NitratesDeclaration, reportingSubject: Subject): Promise<Subject> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/reportingSubject`;
        return (await this.http.post(url, reportingSubject)).data
    }

    public async getCattles(tableA = false): Promise<Cattle[]> {
        const url = `/nitrates-declaration-api/api/v1/data/cattles${tableA ? '?tableA=true':''}`;
        return (await this.http.get(url)).data
    }

    public async getTreatmentLines(pkCuaa: number, nd: NitratesDeclaration): Promise<TreatmentLine[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/treatmentLines`;
        return (await this.http.get(url)).data
    }

    public async setCattlesNodes(pkCuaa: number, cattle: string, cattles: CattleNode[], year: number | Date): Promise<Cattle[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/cattles/${cattle}/year/${year}`;
        return (await this.http.post(url, cattles)).data
    }

    // BEGIN TABLES
    
    // P2 TABLE A
    public async getTableA(pkCuaa: number, filtered = false, nd: NitratesDeclaration, cattle: string): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/p2tableA/cattles/${cattle}/${filtered ? '?filtered=true' : ''}`;
        return (await this.http.get(url)).data
    }
    
    public async getAllTableA(pkCuaa: number, filtered = false, nd: NitratesDeclaration): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/p2tableA${filtered ? '?filtered=true' : ''}`;
        return (await this.http.get(url)).data
    }

    public async setTableA(pkCuaa: number, cattles: CattleNode[], nd: NitratesDeclaration, cattle: string): Promise<Cattle[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/p2tableA/cattles/${cattle}`;
        return (await this.http.post(url, cattles)).data
    }

    public async getStorageContainers(pkCuaa: number, nd: NitratesDeclaration): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/storageContainers`;
        return (await this.http.get(url)).data
    }

    public async getStorageSituations(pkCuaa: number, nd: NitratesDeclaration): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/storageSituation`;
        return (await this.http.get(url)).data
    }

    public async setStorageSituations(pkCuaa: number, nd: NitratesDeclaration, situations: StorageSituation[]): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/storageSituation`;
        return (await this.http.post(url, situations)).data
    }

    // P3 TABLE C
    public async getTableC(pkCuaa: number, filtered = false, nd: NitratesDeclaration, cattle: string): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/p3tableC/cattles/${cattle}/${filtered ? '?filtered=true' : ''}`;
        return (await this.http.get(url)).data
    }
    
    public async getAllTableC(pkCuaa: number, filtered = false, nd: NitratesDeclaration): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/p3tableC${filtered ? '?filtered=true' : ''}`;
        return (await this.http.get(url)).data
    }
    
    public async setTableC(pkCuaa: number, cattles: CattleNode[], nd: NitratesDeclaration, cattle: string): Promise<Cattle[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/p3tableC/cattles/${cattle}`;
        return (await this.http.post(url, cattles)).data
    }

    // P3 TABLE D
    public async getTableD(pkCuaa: number, nd: NitratesDeclaration): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/p3tableD`;
        return (await this.http.get(url)).data
    }

    // P3 TABLE E
    public async getTableE(pkCuaa: number, nd: NitratesDeclaration): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/p3tableE`;
        return (await this.http.get(url)).data
    }

    // NITROGEN SOIL
    public async getNitrogenSoil(pkCuaa: number, nd: NitratesDeclaration): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/nitrogenSoil`;
        return (await this.http.get(url)).data
    }

    public async confirmNitrogenSoil(pkCuaa: number, nd: NitratesDeclaration): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/nitrogenSoil`;
        return (await this.http.post(url)).data
    }
    
    // P4 TABLE I
    public async getTableI(pkCuaa: number, nd: NitratesDeclaration): Promise<ItemI> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/p4TableI`;
        return (await this.http.get(url)).data
    }

    public async setTableI(pkCuaa: number, nd: NitratesDeclaration, body: ItemI): Promise<ItemI> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/p4TableI`;
        return (await this.http.post(url, body)).data
    }

    // P3 TABLE LM
    public async getTableLM(pkCuaa: number, nd: NitratesDeclaration, soldPurchase: "sold" | "purchased"): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/effluents/${soldPurchase}`;
        return (await this.http.get(url)).data
    }

    public async getAvailableMachines(pkCuaa: number, paging: PagingParams): Promise<PagedResults<any>> {
        const url = `/sitiagri-rest-api/api_basic/v1/equipments/subjects/${pkCuaa}/equipments?count=${paging.count}&offset=${paging.offset}`;
        return (await this.http.get(url)).data
    }

    public async getMachines(pkCuaa: number, nd: NitratesDeclaration): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/spreadings/machines`;
        return (await this.http.get(url)).data
    }

    public async setMachines(pkCuaa: number, nd: NitratesDeclaration, machineId: number): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/spreadings/machines/${machineId}`;
        return (await this.http.post(url)).data
    }

    public async removeMachines(pkCuaa: number, nd: NitratesDeclaration, machineId: number): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/spreadings/machines/${machineId}`;
        return (await this.http.delete(url)).data
    }
    
    public async getTableN(pkCuaa: number, nd: NitratesDeclaration): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/spreadings/modes`;
        return (await this.http.get(url)).data
    }

    public async setTableN(pkCuaa: number, nd: NitratesDeclaration, quadri: Quadro[]): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/spreadings/modes`;
        return (await this.http.post(url, quadri)).data
    }

    public async setTableLM(pkCuaa: number, nd: NitratesDeclaration, soldPurchase: "sold" | "purchased", effluents: ItemLM[]): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/effluents/${soldPurchase}`;
        return (await this.http.post(url, effluents)).data
    }

    // P4 TABLE F/C
    public async getSpreadings(pkCuaa: number, nd: NitratesDeclaration): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/spreadings`;
        return (await this.http.get(url)).data
    }
    
    // P4 TABLE G
    public async getFarmSummary(pkCuaa: number, nd: NitratesDeclaration): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/farmSummary`;
        return (await this.http.get(url)).data
    }
    
    public async addSpreading(pkCuaa: number, nd: NitratesDeclaration, spreading: any): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/spreadings`;
        return (await this.http.post(url, spreading)).data
    }

    public async saveEmptySpreading(pkCuaa: number, nd: NitratesDeclaration, mode: string): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/confirmStep/${mode}`;
        return (await this.http.post(url)).data
    }

    public async deleteSpreading(pkCuaa: number, nd: NitratesDeclaration, lotId: number): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/spreadings/lot/${lotId}`;
        return (await this.http.delete(url)).data
    }

    // P2 QUADRO
    public async getQuadri(pkCuaa: number, ndId: number, quadro: "Q1" | "Q2" | "Q3"): Promise<Quadro[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/p2Quadro/${quadro}`;
        return (await this.http.get(url)).data
    }

    public async setQuadri(pkCuaa: number, ndId: number, quadro: "Q1" | "Q2" | "Q3", quadri: Quadro[]): Promise<Quadro[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/p2Quadro/${quadro}`;
        return (await this.http.post(url, quadri)).data
    }

    public async getMaterialByFarm(pkCuaa: number, nd: NitratesDeclaration): Promise<MaterialByFarm> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/p3quadroB3`;
        return (await this.http.get(url)).data
    }

    public async setMaterialByFarm(pkCuaa: number, nd: NitratesDeclaration, quadro: any): Promise<MaterialByFarm> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/p3quadroB3`;
        return (await this.http.post(url, quadro)).data
    }

    // P3 QUADRO4
    public async getQuadro4(pkCuaa: number, ndId: number): Promise<TreatmentItem[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/p3quadro4`;
        return (await this.http.get(url)).data
    }
    
    public async setQuadro4(pkCuaa: number, ndId: number, quadri: TreatmentItem): Promise<TreatmentItem[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/p3quadro4`;
        return (await this.http.post(url, quadri)).data
    }
    
    public async deleteQuadro4(pkCuaa: number, ndId: number, quadro: number): Promise<TreatmentItem[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/p3quadro4/${quadro}`;
        return (await this.http.delete(url)).data
    }

    // P3 QUADRO 5
    public async getQuadro5(pkCuaa: number, ndId: number): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/p3quadro5`;
        return (await this.http.get(url)).data
    }
    
    public async setQuadro5(pkCuaa: number, ndId: number, quadri: any): Promise<TreatmentItem[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/p3quadro5`;
        return (await this.http.post(url, quadri)).data
    }
    
    public async getOriginOfWasteWaters(pkCuaa: number, ndId: number): Promise<OriginWasteWater[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/originOfWasteWaters`;
        return (await this.http.get(url)).data
    }

    public async setOriginOfWasteWaters(pkCuaa: number, ndId: number, originOfWasteWaters: OriginWasteWater[]): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/originOfWasteWaters`;
        return (await this.http.post(url, originOfWasteWaters)).data
    }
    
    // P5 WASTE WATER
    public async getWasteWater(pkCuaa: number, ndId: number): Promise<WasteWater> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/p5WasteWater`;
        return (await this.http.get(url)).data
    }
    
    public async setWasteWater(pkCuaa: number, ndId: number, wasteWater: WasteWater): Promise<WasteWater> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/p5WasteWater`;
        return (await this.http.post(url, wasteWater)).data
    }

    // P5 EXCESS WATER
    public async getWasteWaterDestination(): Promise<WasteWater> {
        const url = `/nitrates-declaration-api/api/v1/data/wasteWaterDestination`;
        return (await this.http.get(url)).data
    }
    // P5 EXCESS WATER
    public async getExcessWater(pkCuaa: number, ndId: number): Promise<ExcessWater[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/p5ExcessWasteWater`;
        return (await this.http.get(url)).data
    }
    
    public async setExcessWater(pkCuaa: number, ndId: number, wasteWater: ExcessWater): Promise<WasteWater[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/p5ExcessWasteWater`;
        return (await this.http.post(url, wasteWater)).data
    }
    
    public async removeExcessWater(pkCuaa: number, ndId: number, waterId: number): Promise<ExcessWater[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/p5ExcessWasteWater/${waterId}`;
        return (await this.http.delete(url)).data
    }
    // END TABLES

    public async getCattleAllNodes(pkCuaa: number, filtered = false, year: number | Date): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/cattles/year/${year}${filtered ? '?filtered=true' : ''}`;
        return (await this.http.get(url)).data
    }

    public async getCattleNodes(pkCuaa: number, cattle: string, year: number | Date): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/cattles/${cattle}/year/${year}`;
        return (await this.http.get(url)).data
    }

    public async getAnomalies(pkCuaa: number, nd: NitratesDeclaration): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/anomalies`;
        return (await this.http.get(url)).data
    }

    public async getModels(): Promise<Model[]> {
        const url = `/nitrates-declaration-api/api/v1/data/declarationModels`;
        return (await this.http.get(url)).data
    }

    public async getModelsByDeclaration(pkCuaa: number, nd: NitratesDeclaration): Promise<Model[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/declarationModels?ndId=${nd.id}`;
        return (await this.http.get(url)).data
    }

    public async downloadModel(modelType: string): Promise<AxiosResponse> {
        const url = `/nitrates-declaration-api/api/v1/data/declarationModels/${modelType}/download/b64`;
        return (await this.http.get(url))
    }
    
    public async downloadFilledModel(pkCuaa: number, nd: NitratesDeclaration): Promise<AxiosResponse> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/documents/${nd.document?.code || 0}/download/b64`;
        return (await this.http.get(url))
    }
    
    public async uploadFilledModel(pkCuaa: number, nd: NitratesDeclaration, formData: FormData | null): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/documents`;
        return (await this.http.post(url, formData)).data
    }
    
    public async getDocumentStatus(pkCuaa: number, year: number): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/documents/status/?docTypes=TR&docTypes=ACCREDIA_ANALISYS${year ? '&year=' + year : ''}`;
        return (await this.http.get(url)).data
    }

    public async getDocument(pkCuaa: number, docType: "TR", year: number): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/documents/type/${docType}${year ? '?year=' + year : ''}`;
        return (await this.http.get(url)).data
    }
    
    public async downloadDocument(pkCuaa: number, code: string): Promise<AxiosResponse> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/documents/${code}/download/b64`;
        return (await this.http.get(url))
    }

    public async uploadDocument(pkCuaa: number, formData: FormData | null, docType: "TR", year: number): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/documents/type/${docType}${year ? '?year=' + year : ''}`;
        return (await this.http.post(url, formData)).data
    }

    public async removeDocument(pkCuaa: number, code: string): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/documents/${code}`;
        return (await this.http.delete(url)).data
    }

    public async recalculateND(pkCuaa: number, ndId: number): Promise<NitratesDeclaration> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/recalculation`;
        return (await this.http.post(url)).data
    }

    public async getND(pkCuaa: number, ndId: number): Promise<NitratesDeclaration> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}`;
        return (await this.http.get(url)).data
    }

    public async getNDList(pkCuaa: number): Promise<NitratesDeclaration[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd`;
        return (await this.http.get(url)).data
    }

    public async deleteND(pkCuaa: number, ndId: number): Promise<NitratesDeclaration[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}`;
        return (await this.http.delete(url)).data
    }

    public async addND(pkCuaa: number, nd: any): Promise<NitratesDeclaration> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd`;
        const params = {
            ...nd
        }
        return (await this.http.post(url, params)).data
    }

    public async editND(pkCuaa: number, nd: any): Promise<NitratesDeclaration> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}`;
        const params = {
            ...nd
        }
        return (await this.http.put(url, params)).data
    }

    public async sendND(pkCuaa: number, nd: NitratesDeclaration, code: string): Promise<NitratesDeclaration> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/status?code=${code}`;
        return (await this.http.put(url)).data
    }

    public async getPlotList(pkCuaa: number, nd: NitratesDeclaration): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/lots`;
        return (await this.http.get(url)).data
    }

    public async getFarmMaterials(pkCuaa: number, nd: NitratesDeclaration, tableType: string): Promise<FarmMaterial[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/farmMaterials/tableType/${tableType}`;
        return (await this.http.get(url)).data
    }

    public async setFarmMaterials(pkCuaa: number, nd: NitratesDeclaration, tableType: string, material: FarmMaterial): Promise<FarmMaterial[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/farmMaterials/tableType/${tableType}`;
        return (await this.http.post(url, material)).data
    }

    public async deleteFarmMaterial(pkCuaa: number, nd: NitratesDeclaration, farmMaterial: FarmMaterial): Promise<FarmMaterial[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/farmMaterials/id/${farmMaterial.id}`;
        return (await this.http.delete(url)).data
    }

    public async getEffluents(pkCuaa: number, nd: NitratesDeclaration, inletDigestates: "EXCEPT_C" | null = null): Promise<Effluent[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/inletDigestates/${inletDigestates ? inletDigestates : ''}`;
        return (await this.http.get(url)).data
    }

    public async setEffluents(pkCuaa: number, nd: NitratesDeclaration, inletDigestates: "EXCEPT_C" | null = null, effluent: Effluent): Promise<Effluent[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/inletDigestates/${inletDigestates ? inletDigestates : ''}`;
        return (await this.http.post(url, effluent)).data
    }

    public async deleteEffluent(pkCuaa: number, nd: NitratesDeclaration, effluent: Effluent): Promise<Effluent[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${nd.id}/inletDigestates/id/${effluent.id}`;
        return (await this.http.delete(url)).data
    }

    public async getPrintTypes(): Promise<PrintType[]> {
        const url = `/nitrates-declaration-api/api/v1/data/prints/types`;
        return (await this.http.get(url)).data
    }

    public async generatePrint(pkCuaa: number, ndId: number, printId: number): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/prints/${printId}`;
        return (await this.http.post(url)).data
    }

    public async printStatus(pkCuaa: number, ndId: number, printId: number): Promise<any> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/prints/${printId}/status`;
        return (await this.http.get(url)).data
    }

    public async downloadPrint(url: string): Promise<any> {
        return (await this.http.get(url)).data
    }

    public async getLastPrints(pkCuaa: number, ndId: number): Promise<any[]> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/prints`;
        return (await this.http.get(url)).data
    }

    public async getNdPrint(pkCuaa: number, ndId: number, printId: number): Promise<{ url: string }> {
        const url = `/nitrates-declaration-api/api/v1/farm/${pkCuaa}/nd/${ndId}/prints/${printId}/url`;
        return (await this.http.get(url)).data
    }

/*
    GET {{url}}/api/v1/farm/{{farmId}}/nd/{{nd_id}}/prints Lista Stampe
    GET {{url}}/api/v1/data/prints/types Tipologie stampe
    GET {{url}}/api/v1/farm/{{farmId}}/pua/{{puaId}}/prints/{printID}/url url dowload stampe
    GET {{url}}/api/v1/farm/{{farmId}}/pua/{{puaId}}/prints/{idStampaLog}/status status stampe
    *PORT /nd/{ndId}/prints/{printTypeId} tipologia stampa da "stampare"

    */
}